// Common interface of the reordering optimizers (todo/03, S1).
//
// A problem is the category-ordering problem of one attribute order: dataset, attribute order, first-attribute
// mode, metric, metric version. The joint problem is the loop over the attribute orders (attributeOrders below).
// A solution is one Int32Array per attribute of the attribute order, mapping the category base index
// (config-id.js) to its position on the axis.
//
// First-attribute modes: 'free'       = the first attribute stays in place, its category order is optimized;
//                        'fixedOrder' = its category order is fixed as well (firstOrder).
// Constraints (todo/10): per attribute a fixed order, pinned categories, or blocks (constraints.js); the
// admissible category orders of attribute k are problem.admissible(k). 'fixedOrder' is the fixed order of the
// first attribute. Fixed positions of attributes restrict the attribute orders (attributeOrders).
import { StructuralEvaluator } from '../metrics/structural.js';
import { METRICS, VERSIONS, isBetter } from '../metrics/directions.js';
import { TOLERANCE, isClose } from '../metrics/tolerance.js';
import { baseOrder, configToId, factorial, permutationUnrank } from '../enumeration/config-id.js';
import { admissibleOrders, buildStructure, constrainedNeighbors, firstOrder, isAdmissible, randomOrder } from './constraints.js';
import { randomPermutation } from './rng.js';

export const FIRST_ATTRIBUTE_MODES = ['free', 'fixedOrder'];
export const CATEGORY_METRICS = Object.keys(METRICS).filter(m => METRICS[m].group === 'category');

const now = () => performance.now();

// All orders of n categories as position arrays (category -> position), in the rank order of config-id.js.
const PERMUTATIONS = [];
export function allPositions(n) {
    if (!PERMUTATIONS[n]) {
        const list = [];
        for (let r = 0; r < factorial(n); r++) {
            const pos = new Int32Array(n);
            permutationUnrank(r, n).forEach((c, i) => { pos[c] = i; });
            list.push(pos);
        }
        PERMUTATIONS[n] = list;
    }
    return PERMUTATIONS[n];
}

// All attribute orders of a base order; firstAttribute (name) restricts them to (|D| - 1)! orders. Instead of a
// name, an object { name: position } fixes the positions of several attributes ((|D| - f)! orders, none if two
// attributes share a position).
export function attributeOrders(base, firstAttribute = null) {
    const fixed = firstAttribute === null ? [] : typeof firstAttribute === 'string' ? [[firstAttribute, 0]] : Object.entries(firstAttribute);
    for (const [name, position] of fixed) {
        if (!base.some(a => a.name === name)) throw new Error('Unknown attribute: ' + name);
        if (!Number.isInteger(position) || position < 0 || position >= base.length) throw new Error('Position out of range: ' + name);
    }
    // The free attributes in the free slots only, in the rank order of config-id.js (the first best order wins on ties).
    const order = new Array(base.length).fill(null);
    for (const [name, position] of fixed) {
        if (order[position] !== null) return [];
        order[position] = base.find(a => a.name === name);
    }
    const free = base.filter(a => !order.includes(a));
    const slots = order.flatMap((a, position) => (a === null ? [position] : []));
    const used = new Array(free.length).fill(false);
    const orders = [];
    const place = s => {
        if (s === slots.length) { orders.push(order.slice()); return; }
        for (let i = 0; i < free.length; i++) {
            if (used[i]) continue;
            used[i] = true; order[slots[s]] = free[i];
            place(s + 1);
            used[i] = false;
        }
    };
    place(0);
    return orders;
}

export class Problem {
    /**
     * @param dataset             dataset from data/dataset-loader.js
     * @param attributeOrder      base attributes ([{ name, categories }]) or attribute names, in display order
     * @param firstAttributeMode  'free' (default) | 'fixedOrder'
     * @param firstOrder          for 'fixedOrder': category names of the first attribute in display order
     *                            (default: base order)
     * @param constraints         { attribute name: { order, pins, blocks } } (constraints.js); default: none
     * @param metric              key of METRICS, category ordering-dependent
     * @param version             'corrected' (the only version; structural evaluators)
     * @param evaluator           optional StructuralEvaluator of the attribute order (shared between problems)
     */
    constructor({ dataset, attributeOrder, firstAttributeMode = 'free', firstOrder = null, constraints = null, metric, version = 'corrected',
        layoutOptions = {}, evaluator = null }) {
        if (!CATEGORY_METRICS.includes(metric)) throw new Error('Not a category ordering-dependent metric: ' + metric);
        if (!VERSIONS.includes(version)) throw new Error('Unknown metric version: ' + version);
        if (!FIRST_ATTRIBUTE_MODES.includes(firstAttributeMode)) throw new Error('Unknown first-attribute mode: ' + firstAttributeMode);
        this.dataset = dataset;
        this.base = baseOrder(dataset);
        this.attributeOrder = attributeOrder.map(a => {
            const attribute = this.base.find(b => b.name === (typeof a === 'string' ? a : a.name));
            if (!attribute) throw new Error('Unknown attribute: ' + (typeof a === 'string' ? a : a.name));
            return attribute;
        });
        this.nd = this.attributeOrder.length;
        this.sizes = this.attributeOrder.map(a => a.categories.length);
        this.metric = metric;
        this.version = version;
        this.direction = METRICS[metric].direction;
        this.column = METRICS[metric][version].column;
        this.layoutOptions = layoutOptions;
        this.firstAttributeMode = firstAttributeMode;
        for (const name of Object.keys(constraints || {})) if (!this.base.some(b => b.name === name)) throw new Error('Unknown attribute: ' + name);
        // structures[k]: constraints of attribute k (null: every category order is admissible).
        this.structures = this.attributeOrder.map((a, k) => {
            if (k === 0 && firstAttributeMode === 'fixedOrder') return buildStructure(a.categories, { order: (firstOrder || a.categories).map(c => (typeof c === 'string' ? c : c.name)) });
            return buildStructure(a.categories, constraints && Object.hasOwn(constraints, a.name) ? constraints[a.name] : undefined);
        });
        // free[k]: the category order of attribute k is a variable.
        this.free = this.sizes.map((n, k) => n > 1 && !(k === 0 && firstAttributeMode === 'fixedOrder') && (!this.structures[k] || this.structures[k].freeUnits.length > 1));
        // Mirror symmetry of the metrics can be used only if every category order is admissible (a pin, a block, or a
        // fixed order breaks it; fixed positions of attributes do not, they restrict the attribute orders only).
        this.symmetric = firstAttributeMode === 'free' && this.structures.every(s => !s);
        this.evaluator = evaluator || new StructuralEvaluator(dataset, this.attributeOrder, layoutOptions);
        this.resetStats();
    }

    positionsOf(baseCategories, categories) {
        const pos = new Int32Array(baseCategories.length).fill(-1);
        categories.forEach((c, i) => {
            const index = baseCategories.indexOf(typeof c === 'string' ? c : c.name);
            if (index < 0) throw new Error('Unknown category: ' + c);
            pos[index] = i;
        });
        if (pos.includes(-1)) throw new Error('Category order is not a permutation of the base order.');
        return pos;
    }

    // --- evaluation --------------------------------------------------------------------------------------------

    // Counters of one run; target = known optimum (to record the first hit), trace = record every improvement
    // as [evaluations, value, ms since the reset].
    resetStats({ target = null, tolerance = TOLERANCE, trace = false } = {}) {
        this.evaluations = 0;
        this.best = null;
        this.target = target;
        this.tolerance = tolerance;
        this.firstHitEvaluations = null;
        this.firstHitTime = null;
        this.trace = trace ? [] : null;
        this.startTime = now();
    }

    // Uncounted metric value.
    value(solution) {
        return this.evaluator.evaluate(this.metric, solution);
    }

    // Counted metric value; every optimizer evaluates through this function.
    evaluate(solution) {
        const value = this.value(solution);
        this.evaluations++;
        if (this.best === null || isBetter(this.metric, value, this.best) || (Number.isNaN(this.best) && !Number.isNaN(value))) {
            this.best = value;
            if (this.trace) this.trace.push([this.evaluations, value, now() - this.startTime]);
            if (this.target !== null && this.firstHitEvaluations === null && isClose(value, this.target, this.tolerance)) {
                this.firstHitEvaluations = this.evaluations;
                this.firstHitTime = now() - this.startTime;
            }
        }
        return value;
    }

    // True if a is better than b by more than the tolerance (accepting only such moves makes every descent finite).
    improves(a, b) {
        return isBetter(this.metric, a, b) && !isClose(a, b, TOLERANCE);
    }

    // --- solutions ---------------------------------------------------------------------------------------------

    // Base order; for a constrained attribute, its first admissible order.
    baseSolution() {
        return this.sizes.map((n, k) => (this.structures[k] ? firstOrder(this.structures[k]).slice() : Int32Array.from({ length: n }, (_, i) => i)));
    }

    randomSolution(rand) {
        return this.sizes.map((n, k) => {
            if (!this.structures[k]) return randomPermutation(rand, n);
            return (this.free[k] ? randomOrder(this.structures[k], rand) : firstOrder(this.structures[k])).slice();
        });
    }

    // Admissible category orders of attribute k (position arrays); all orders if it is unconstrained.
    admissible(k) {
        return this.structures[k] ? admissibleOrders(this.structures[k]) : allPositions(this.sizes[k]);
    }

    // Pairs of categories in the same order in every admissible order (todo/17), per attribute: Int8Array
    // (c1 * n + c2) with +1 = c1 before c2, -1 = after, 0 = both occur; null if the attribute has no such pair.
    forcedPairs() {
        if (this._forcedPairs) return this._forcedPairs;
        this._forcedPairs = this.sizes.map((n, k) => {
            if (!this.structures[k]) return null;
            const orders = this.free[k] ? this.admissible(k) : [this.baseSolution()[k]];
            const forced = new Int8Array(n * n);
            let any = false;
            for (let c1 = 0; c1 < n; c1++) for (let c2 = 0; c2 < n; c2++) {
                if (c1 === c2) continue;
                const first = orders[0][c1] < orders[0][c2];
                if (orders.every(pos => (pos[c1] < pos[c2]) === first)) { forced[c1 * n + c2] = first ? 1 : -1; any = true; }
            }
            return any ? forced : null;
        });
        return this._forcedPairs;
    }

    // Neighbors of a category order of a constrained attribute k, or null if it is unconstrained (local-search.js).
    constrainedNeighbors(k, pos, neighborhood) {
        return this.structures[k] ? constrainedNeighbors(this.structures[k], pos, neighborhood) : null;
    }

    // True if a solution satisfies the constraints.
    feasible(solution) {
        return this.structures.every((s, k) => !s || isAdmissible(s, solution[k]));
    }

    copy(solution) {
        return solution.map(pos => pos.slice());
    }

    fromConfig(config) {
        return this.attributeOrder.map((a, k) => {
            if (config[k].name !== a.name) throw new Error('Configuration has a different attribute order.');
            return this.positionsOf(a.categories, config[k].categories);
        });
    }

    toConfig(solution) {
        return this.attributeOrder.map((a, k) => {
            const categories = new Array(a.categories.length);
            a.categories.forEach((c, i) => { categories[solution[k][i]] = c; });
            return { name: a.name, categories };
        });
    }

    toId(solution) {
        return configToId(this.toConfig(solution), this.base);
    }

    // Number of feasible configurations of the problem.
    get configurations() {
        return this.sizes.reduce((acc, n, k) => acc * (!this.free[k] ? 1 : this.structures[k] ? this.admissible(k).length : factorial(n)), 1);
    }
}

// Result record shared by the optimizers.
export function result(problem, optimizer, solution, value, termination, extra = {}) {
    return {
        optimizer, solution, value, termination,
        evaluations: problem.evaluations,
        time: now() - problem.startTime,
        firstHitEvaluations: problem.firstHitEvaluations,
        firstHitTime: problem.firstHitTime,
        ...extra,
    };
}
